import React, { useState, useEffect } from "react";
import { getRateCatalogItems, upsertRateCatalogItem, deleteRateCatalogItem } from "./supabase";
import { getCategoryFields, validateRateItem, validateRateField, toRateData } from "./rateValidation";

const CATEGORIES = [
  { key: "paint_product", label: "Paint Products" },
  { key: "labor_rate", label: "Labor Rates" },
  { key: "prep_severity_tier", label: "Prep Severity Tiers" },
  { key: "markup", label: "Markup" },
];

// vocabulary is threaded for client vocabulary; no module noun rendered here yet.
export default function RateCatalog({ projectId, isAdmin, onChange, vocabulary = {} }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  async function refresh() {
    setItems(await getRateCatalogItems(projectId));
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRateCatalogItems(projectId).then((rows) => {
      if (!cancelled) {
        setItems(rows);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [projectId]);

  async function handleSave(item) {
    const { error } = await upsertRateCatalogItem(item, projectId);
    if (error) { alert("Failed to save rate catalog item." + (error.message ? ` (${error.message})` : "")); return; }
    setEditing(null);
    await refresh();
    onChange?.();
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this rate catalog item?")) return;
    const { error } = await deleteRateCatalogItem(id);
    if (error) { alert("Failed to delete rate catalog item." + (error.message ? ` (${error.message})` : "")); return; }
    await refresh();
    onChange?.();
  }

  if (loading) return <div>Loading rate catalog…</div>;

  return (
    <div className="rate-catalog">
      {CATEGORIES.map((cat) => (
        <section key={cat.key} style={{ marginBottom: 24 }}>
          <h3>{cat.label}</h3>
          <ul>
            {items.filter((it) => it.category === cat.key).map((it) => (
              <li key={it.id} style={{ marginBottom: 4 }}>
                <strong>{it.name}</strong>{" "}
                <code>{JSON.stringify(it.data)}</code>{" "}
                {isAdmin && (
                  <>
                    <button onClick={() => setEditing(it)}>Edit</button>{" "}
                    <button onClick={() => handleDelete(it.id)}>Delete</button>
                  </>
                )}
              </li>
            ))}
          </ul>
          {isAdmin && (
            <button onClick={() => setEditing({ category: cat.key, name: "", data: Object.fromEntries(getCategoryFields(cat.key, {}).map((f) => [f.key, f.type === "boolean" ? false : ""])) })}>
              + Add to {cat.label}
            </button>
          )}
        </section>
      ))}
      {editing && (
        <RateCatalogItemEditor item={editing} onCancel={() => setEditing(null)} onSave={handleSave} />
      )}
    </div>
  );
}

function RateCatalogItemEditor({ item, onCancel, onSave }) {
  const [name, setName] = useState(item.name || "");
  const [data, setData] = useState({ ...item.data });
  const [nameTouched, setNameTouched] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState([]);
  // Recomputed from data on every render, not cached: labor_rate's own
  // field list depends on data.task_type (see getCategoryFields), so the
  // Productivity field must appear/disappear/re-key as she picks Paint vs
  // Prep, and every field's inline error must track her latest keystroke.
  const fields = getCategoryFields(item.category, data);

  function setField(key, value) {
    setData((d) => ({ ...d, [key]: value }));
    setTouched((t) => ({ ...t, [key]: true }));
  }

  function submit(e) {
    e.preventDefault();
    setSubmitted(true);
    const fieldErrors = validateRateItem(item.category, data);
    if (!name.trim()) fieldErrors.unshift(`"Name" is required.`);
    if (fieldErrors.length > 0) { setErrors(fieldErrors); return; }
    setErrors([]);
    onSave({ ...item, name, data: toRateData(item.category, data) });
  }

  const nameError = !name.trim() ? `"Name" is required.` : null;

  return (
    <form onSubmit={submit} style={{ border: "1px solid var(--rf-border)", borderRadius: 8, padding: 16, marginTop: 12 }}>
      <div style={{ marginBottom: 8 }}>
        <label>Name<br />
          <input
            value={name}
            onChange={(e) => { setName(e.target.value); setNameTouched(true); }}
            required
            style={{ width: "100%" }}
          />
        </label>
        {(nameTouched || submitted) && nameError && (
          <div style={{ color: "var(--rf-danger)", fontSize: "0.9em" }}>{nameError}</div>
        )}
      </div>
      {fields.map((f) => {
        const fieldError = validateRateField(item.category, f.key, data[f.key], data);
        const showFieldError = (touched[f.key] || submitted) && fieldError;
        return (
          <div key={f.key} style={{ marginBottom: 8 }}>
            <label>{f.label}<br />
              {f.type === "select" ? (
                <select value={data[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} style={{ width: "100%" }}>
                  <option value="">Choose...</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === "boolean" ? (
                <input type="checkbox" checked={!!data[f.key]} onChange={(e) => setField(f.key, e.target.checked)} />
              ) : f.type === "number" ? (
                // Kept as raw text while she types, not a controlled
                // type="number" input: parsing on every keystroke turns a
                // partial decimal like "38." into 38, which re-renders as
                // "38" and mangles a typed "38.5" into "385". Converted to
                // a real number only at save time by toRateData().
                <input
                  type="text"
                  inputMode="decimal"
                  value={data[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  style={{ width: "100%" }}
                />
              ) : (
                <input value={data[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} style={{ width: "100%" }} />
              )}
            </label>
            {showFieldError && <div style={{ color: "var(--rf-danger)", fontSize: "0.9em" }}>{fieldError}</div>}
          </div>
        );
      })}
      {errors.length > 0 && (
        <ul style={{ color: "var(--rf-danger)", marginBottom: 8 }}>
          {errors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}
      <button type="submit">Save</button>{" "}
      <button type="button" onClick={onCancel}>Cancel</button>
    </form>
  );
}
